import React, { ReactElement } from 'react'
import { PayoutEntryHedge } from '../../../state/Radom'
import { formatAssetAmount } from '../../../util/Util'
import DetailsRow from './DetailsRow'
import SummaryRow from './SummaryRow'
import { computeHedgedPayoutCosts } from './hedgedPayoutCosts'

export const HedgedPayoutCostBreakdown = ({
  hedge,
  cryptoTicker,
  recipientAmount,
  compact = false
}: {
  hedge: PayoutEntryHedge
  cryptoTicker: string
  recipientAmount?: number | string
  compact?: boolean
}): ReactElement => {
  const fundingTicker = hedge.fundingCurrency
  const costs = computeHedgedPayoutCosts(hedge, recipientAmount)

  const rows: Array<{ title: string, value: string, summary?: boolean }> = [
    {
      title: 'Recipient receives',
      value: `${formatAssetAmount(costs.guaranteedAmount, cryptoTicker)} ${cryptoTicker}`
    },
    {
      title: 'Rate',
      value: costs.lockedRate > 0
        ? `1 ${cryptoTicker} = ${formatAssetAmount(costs.lockedRate, fundingTicker)} ${fundingTicker}`
        : '—'
    }
  ]

  if (costs.networkFee > 0) {
    rows.push({
      title: 'Network fee',
      value: `${formatAssetAmount(costs.networkFee, fundingTicker, 6)} ${fundingTicker}`
    })
  }

  if (costs.radomFee > 0) {
    rows.push({
      title: 'Radom fee',
      value: `${formatAssetAmount(costs.radomFee, fundingTicker, 6)} ${fundingTicker}`
    })
  }

  rows.push({
    title: 'You pay',
    value: `${formatAssetAmount(costs.stablecoinCost, fundingTicker, 6)} ${fundingTicker}`,
    summary: true
  })

  if (compact) {
    return (
      <>
        {rows.map(row => row.summary
          ? <SummaryRow key={row.title} title={row.title} value={row.value} />
          : <DetailsRow key={row.title} title={row.title} value={row.value} />
        )}
      </>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map(row => (
        <div
          key={row.title}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            ...(row.summary ? { paddingTop: 8, borderTop: '1px solid #eef1f5' } : {})
          }}
        >
          <span style={{ color: row.summary ? '#20242d' : '#5f6675', fontSize: 13, fontWeight: row.summary ? 500 : 400 }}>
            {row.title}
          </span>
          <span style={{
            color: '#20242d',
            fontSize: 13,
            fontWeight: 500,
            textAlign: 'right'
          }}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  )
}

export default HedgedPayoutCostBreakdown
